import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND, GOLD_GRADIENT, IVORY_GRADIENT_TEXT, Kicker } from '../common/ui';

const CAST_STRIP = ['trace', 'mina', 'flash', 'prince', 'cass', 'juss', 'tim'];

export default function CoverPage() {
  const { isAuthenticated, isAdmin } = useAuth();
  if (isAuthenticated) return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', background: BRAND.navy900, fontFamily: BRAND.font, color: BRAND.ivory }}>
      <motion.img
        src={BRAND.bg.school} alt="" aria-hidden
        initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 14, ease: 'linear' }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }}
      />
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.96) 0%, rgba(7,20,45,0.82) 42%, rgba(7,20,45,0.25) 100%), linear-gradient(180deg, rgba(7,20,45,0.2) 0%, transparent 30%, rgba(7,20,45,0.85) 100%)' }} />

      {/* top bar */}
      <header style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'clamp(16px,2.4vw,28px) clamp(20px,4vw,64px)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <img src={BRAND.owl} alt="" style={{ width: 44, height: 44, filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.5))' }} />
          <span style={{ fontWeight: 800, letterSpacing: '0.28em', color: BRAND.goldLight, fontSize: '0.95rem' }}>C.O.R.E.</span>
          <span style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.15rem', color: BRAND.muted }}>for Schools</span>
        </span>
        <a className="core-cover-ext" href="https://www.core-aiowl.org" target="_blank" rel="noreferrer" style={{ fontSize: '0.84rem', fontWeight: 700, color: BRAND.muted, borderBottom: `1px solid ${BRAND.line}`, paddingBottom: 2 }}>core-aiowl.org ↗</a>
      </header>

      {/* hero */}
      <main style={{ position: 'relative', zIndex: 2, padding: 'clamp(24px,6vh,80px) clamp(20px,4vw,64px) 0', maxWidth: 760 }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <Kicker>Cyber Operational Risk Education · Grades 6–7</Kicker>
          <h1 style={{ fontSize: 'clamp(2.6rem,6vw,4.6rem)', lineHeight: 1.02, fontWeight: 900, margin: '14px 0 16px', ...IVORY_GRADIENT_TEXT, filter: 'drop-shadow(0 2px 14px rgba(0,0,0,0.45))' }}>
            Learn, play,<br />protect.
          </h1>
          <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: 'clamp(1.2rem,2vw,1.7rem)', lineHeight: 1.3, color: 'rgba(249,240,231,0.9)', maxWidth: 560 }}>
            Eighteen cinematic lessons with Trace, Mina and the whole team — missions, badges and real-life scenarios to become the most careful person in the room.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 34, flexWrap: 'wrap' }}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '17px 36px', borderRadius: 999, background: GOLD_GRADIENT, color: BRAND.navy900, fontWeight: 900, fontSize: '1.1rem', boxShadow: '0 10px 40px rgba(205,155,80,0.45)' }}>
                Enter the School <FiArrowRight size={20} />
              </Link>
            </motion.div>
            <span style={{ fontSize: '0.86rem', color: BRAND.muted, fontWeight: 600 }}>Students use the code from their teacher.</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }} style={{ display: 'flex', gap: 'clamp(20px,3vw,40px)', marginTop: 'clamp(40px,8vh,96px)', flexWrap: 'wrap' }}>
          {[['18', 'lessons'], ['90+', 'missions'], ['100', 'badges to earn'], ['11', 'characters']].map(([n, l]) => (
            <div key={l}>
              <b style={{ display: 'block', fontSize: '1.7rem', color: BRAND.goldLight, lineHeight: 1 }}>{n}</b>
              <span style={{ fontSize: '0.74rem', color: BRAND.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>{l}</span>
            </div>
          ))}
        </motion.div>
      </main>

      {/* cast strip */}
      <style>{`@media (max-width: 720px){
        .core-cover-cast{ position: relative !important; right: auto !important; bottom: auto !important; margin: 36px clamp(20px,4vw,64px) 28px; flex-wrap: wrap; }
        .core-cover-ext{ display: none !important; }
      }`}</style>
      <motion.div className="core-cover-cast" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
        style={{ position: 'absolute', right: 'clamp(16px,4vw,64px)', bottom: 'clamp(18px,4vh,40px)', zIndex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '0.74rem', color: BRAND.muted, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginRight: 6 }}>Meet the cast</span>
        <div style={{ display: 'flex' }}>
          {CAST_STRIP.map((k, i) => (
            <img key={k} src={`/characters/cast/${k}.png`} alt={k} title={k} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${BRAND.gold}`, background: BRAND.navy700, marginLeft: i ? -10 : 0, boxShadow: '0 6px 18px rgba(0,0,0,0.5)' }} />
          ))}
        </div>
      </motion.div>

    </div>
  );
}
